import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { getAcademyWithBranches } from "services/usersServices";
import { sanitizeStoredAcademyBranchIds } from "utils/academyBranchesStorage";

export type AcademyId = number | string;

export type AcademyBranchOption = {
  label: string;
  value: AcademyId;
  isMainBranch?: boolean;
};

const ACADEMY_BRANCH_PAGE_SIZE = 50;

const mapAcademyBranchItems = (items: any[] = []): AcademyBranchOption[] =>
  items.map((academy: any) => ({
    label: academy.Name,
    value: academy.AcademyId,
    isMainBranch: !!academy.IsMainBranch,
  }));

type AcademyBranchesContextValue = {
  academyBranches: AcademyBranchOption[];
  isLoading: boolean;
  isLoaded: boolean;
  ensureAcademyBranchesLoaded: () => Promise<AcademyBranchOption[]>;
  refreshAcademyBranches: () => Promise<AcademyBranchOption[]>;
  hasMoreItems: boolean;
  isLoadingMore: boolean;
  loadMoreAcademyBranches: () => Promise<void>;
  getAcademyBranchesForUser: (
    userId: string,
    forceRefresh?: boolean
  ) => Promise<AcademyBranchOption[]>;
};

export const AcademyBranchesContext =
  createContext<AcademyBranchesContextValue | null>(null);

interface Props {
  children: React.ReactNode;
}

const AcademyBranchesProvider: React.FC<Props> = ({ children }) => {
  const [academyBranches, setAcademyBranches] = useState<AcademyBranchOption[]>(
    []
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasMoreItems, setHasMoreItems] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const loadPromiseRef = useRef<Promise<AcademyBranchOption[]> | null>(null);
  const pageRef = useRef(0);
  const hasMoreItemsRef = useRef(false);
  const branchesRef = useRef<AcademyBranchOption[]>([]);
  const sanitizedForTokenRef = useRef<string | null>(null);
  const sanitizeInProgressRef = useRef(false);
  const branchesByUserIdRef = useRef<Map<string, AcademyBranchOption[]>>(
    new Map(),
  );
  const branchesByUserIdPromiseRef = useRef<
    Map<string, Promise<AcademyBranchOption[]>>
  >(new Map());

  const loadPage = useCallback(async (pageNumber: number, append: boolean) => {
    const academyWithBranches: any = await getAcademyWithBranches({
      pageNumber,
      pageSize: ACADEMY_BRANCH_PAGE_SIZE,
    });

    const mapped = mapAcademyBranchItems(academyWithBranches?.Items ?? []);

    setAcademyBranches((prev) => {
      const next = append ? [...prev, ...mapped] : mapped;
      branchesRef.current = next;
      return next;
    });
    hasMoreItemsRef.current = !!academyWithBranches?.HasNextPage;
    setHasMoreItems(hasMoreItemsRef.current);
    pageRef.current = pageNumber;

    return branchesRef.current;
  }, []);

  const ensureAcademyBranchesLoaded = useCallback(async () => {
    if (isLoaded) {
      return branchesRef.current;
    }

    if (loadPromiseRef.current) {
      return loadPromiseRef.current;
    }

    setIsLoading(true);

    loadPromiseRef.current = loadPage(1, false)
      .then((branches) => {
        setIsLoaded(true);
        return branches;
      })
      .catch((error) => {
        console.error(error);
        return branchesRef.current;
      })
      .finally(() => {
        setIsLoading(false);
        loadPromiseRef.current = null;
      });

    return loadPromiseRef.current;
  }, [isLoaded, loadPage]);

  const loadAllAcademyBranches = useCallback(async () => {
    await ensureAcademyBranchesLoaded();

    while (hasMoreItemsRef.current) {
      await loadPage(pageRef.current + 1, true);
    }

    return branchesRef.current;
  }, [ensureAcademyBranchesLoaded, loadPage]);

  const sanitizeStoredAcademyIdsForCurrentUser = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token || sanitizeInProgressRef.current) {
      return branchesRef.current;
    }

    if (sanitizedForTokenRef.current === token) {
      return branchesRef.current;
    }

    sanitizeInProgressRef.current = true;

    try {
      const allBranches = await loadAllAcademyBranches();
      sanitizeStoredAcademyBranchIds(allBranches);
      sanitizedForTokenRef.current = token;
      return allBranches;
    } catch (error) {
      console.error(error);
      return branchesRef.current;
    } finally {
      sanitizeInProgressRef.current = false;
    }
  }, [loadAllAcademyBranches]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      sanitizedForTokenRef.current = null;
      return;
    }

    if (sanitizedForTokenRef.current === token) {
      return;
    }

    void sanitizeStoredAcademyIdsForCurrentUser();
  }, [sanitizeStoredAcademyIdsForCurrentUser]);

  const refreshAcademyBranches = useCallback(async () => {
    setIsLoaded(false);
    pageRef.current = 0;
    branchesRef.current = [];
    hasMoreItemsRef.current = false;
    setAcademyBranches([]);
    setHasMoreItems(false);
    sanitizedForTokenRef.current = null;
    await loadAllAcademyBranches();
    sanitizeStoredAcademyBranchIds(branchesRef.current);
    const token = localStorage.getItem("token");
    if (token) {
      sanitizedForTokenRef.current = token;
    }
    setIsLoaded(true);
    return branchesRef.current;
  }, [loadAllAcademyBranches]);

  const loadMoreAcademyBranches = useCallback(async () => {
    if (!hasMoreItems || isLoadingMore || isLoading) {
      return;
    }

    setIsLoadingMore(true);
    try {
      await loadPage(pageRef.current + 1, true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMoreItems, isLoadingMore, isLoading, loadPage]);

  const getAcademyBranchesForUser = useCallback(
    async (userId: string, forceRefresh = false) => {
    const safeUserId = String(userId ?? "").trim();
    if (!safeUserId) {
      return [];
    }

    if (forceRefresh) {
      branchesByUserIdRef.current.delete(safeUserId);
    }

    const cached = branchesByUserIdRef.current.get(safeUserId);
    if (cached) {
      return cached;
    }

    const cachedPromise = branchesByUserIdPromiseRef.current.get(safeUserId);
    if (cachedPromise) {
      return cachedPromise;
    }

    const promise = (async () => {
      try {
        const academyWithBranches: any = await getAcademyWithBranches({
          userId: safeUserId,
        });
        const mapped = mapAcademyBranchItems(academyWithBranches?.Items ?? []);
        branchesByUserIdRef.current.set(safeUserId, mapped);
        return mapped;
      } catch (error) {
        console.error(error);
        return [];
      } finally {
        branchesByUserIdPromiseRef.current.delete(safeUserId);
      }
    })();

    branchesByUserIdPromiseRef.current.set(safeUserId, promise);
    return promise;
  },
  []);

  const value: AcademyBranchesContextValue = {
    academyBranches,
    isLoading,
    isLoaded,
    ensureAcademyBranchesLoaded,
    refreshAcademyBranches,
    hasMoreItems,
    isLoadingMore,
    loadMoreAcademyBranches,
    getAcademyBranchesForUser,
  };

  return (
    <AcademyBranchesContext.Provider value={value}>
      {children}
    </AcademyBranchesContext.Provider>
  );
};

export const useAcademyBranches = (): AcademyBranchesContextValue => {
  const context = useContext(AcademyBranchesContext);

  if (!context) {
    throw new Error(
      "useAcademyBranches must be used within AcademyBranchesProvider"
    );
  }

  return context;
};

export default AcademyBranchesProvider;
